import React, {useEffect, useId, useRef, useState} from 'react';
import ReactDOM from 'react-dom';
import {PRISMAX_DATA_PIPELINE_URL} from '../../configs/config';
import {getApiErrorMessage, isSessionExpiredError} from '../../utils/authErrors';
import styles from './AdminUploadEpisodeModal.module.css';

const CAMERA_LOADING_SLOTS = ['left', 'env', 'right'];

function formatText(value) {
    const text = String(value ?? '').trim();
    return text || '-';
}

function formatOverviewValue(value) {
    const text = String(value ?? '').trim();
    if (!text) return '-';
    const separator = text.includes(';') ? ';' : ',';
    if (!text.includes(separator)) return text;
    return text.split(separator).map((part) => part.trim()).filter(Boolean).join(', ') || '-';
}

function formatScore(value) {
    if (value === null || value === undefined || String(value).trim() === '') return '-';
    const score = Number(value);
    if (!Number.isFinite(score)) return '-';
    return score % 1 === 0 ? String(score) : score.toFixed(1);
}

function formatClock(totalSeconds) {
    const seconds = Math.max(0, Math.floor(Number(totalSeconds) || 0));
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const rest = seconds % 60;
    if (hours > 0) return `${hours}:${String(minutes).padStart(2, '0')}:${String(rest).padStart(2, '0')}`;
    return `${minutes}:${String(rest).padStart(2, '0')}`;
}

function formatBytes(bytes) {
    const value = Number(bytes || 0);
    if (!Number.isFinite(value) || value <= 0) return '0 B';
    const units = ['B', 'KiB', 'MiB', 'GiB', 'TiB'];
    let size = value;
    let unitIndex = 0;
    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }
    return `${size.toFixed(size >= 10 || unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
}

function getCameraLabel(video) {
    const slot = String(video?.slot || '').trim().toLowerCase();
    const objectName = String(video?.object_name || '').trim().toLowerCase();
    if (slot === 'left' || objectName.includes('left')) return 'left';
    if (slot === 'right' || objectName.includes('right')) return 'right';
    if (objectName.includes('high')) return 'high';
    return slot === 'env' ? 'env' : 'camera';
}

function getCameraOrder(video) {
    const label = getCameraLabel(video);
    if (label === 'left') return 0;
    if (label === 'high' || label === 'env') return 1;
    if (label === 'right') return 2;
    return 3;
}

function DatasetIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <rect x="1" y="1" width="6" height="6" rx="1"/>
            <rect x="9" y="1" width="6" height="6" rx="1"/>
            <rect x="1" y="9" width="6" height="6" rx="1"/>
            <rect x="9" y="9" width="6" height="6" rx="1"/>
        </svg>
    );
}

function RobotIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <path d="M3 13V7a2 2 0 012-2h2V3a1 1 0 011-1h2a1 1 0 011 1v2h2a2 2 0 012 2v6"/>
            <path d="M1 13h14"/>
        </svg>
    );
}

function FileIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <rect x="2" y="1" width="9" height="13" rx="1.5"/>
            <path d="M5 6h4M5 8.5h4M5 11h2"/>
        </svg>
    );
}

function McapIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.4" aria-hidden="true">
            <path d="M2 10h2l2-6 2 9 2-5 1 2h3"/>
        </svg>
    );
}

function CheckIcon() {
    return (
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <path d="M3 8l4 4 6-7"/>
        </svg>
    );
}

export default function AdminUploadEpisodeModal({
    adminToken,
    previewToken,
    adminMode = true,
    episode,
    upload,
    previewImageUrl = '',
    isSelected = false,
    selectDisabled = false,
    onToggleSelect,
    onClose,
    onSessionExpired,
}) {
    const [cameraVideos, setCameraVideos] = useState([]);
    const [isLoadingCameras, setIsLoadingCameras] = useState(true);
    const [detail, setDetail] = useState(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(true);
    const [detailError, setDetailError] = useState('');
    const [isPlaying, setIsPlaying] = useState(true);
    const [currentTime, setCurrentTime] = useState(0);
    const [videoDuration, setVideoDuration] = useState(0);
    const [activeCameraIndex, setActiveCameraIndex] = useState(0);
    const videoRefs = useRef([]);
    const cameraStripRef = useRef(null);
    const onSessionExpiredRef = useRef(onSessionExpired);
    const titleId = useId();
    const episodeId = String(episode?.episode_id || '').trim();
    const isRejected = episode?.status === 'QA_REJECTED';
    const authToken = adminMode ? adminToken : previewToken;
    const [previewError, setPreviewError] = useState('');

    useEffect(() => {
        onSessionExpiredRef.current = onSessionExpired;
    }, [onSessionExpired]);

    useEffect(() => {
        if (!episodeId || !authToken) {
            setCameraVideos([]);
            setDetail(null);
            setIsLoadingCameras(false);
            setIsLoadingDetail(false);
            setPreviewError('Preview unavailable. Please sign in again.');
            return undefined;
        }
        const controller = new AbortController();
        const headers = {Authorization: `Bearer ${authToken}`};
        setCameraVideos([]);
        setIsLoadingCameras(true);
        setDetail(null);
        setIsLoadingDetail(true);
        setDetailError('');
        setPreviewError('');
        setIsPlaying(true);
        setCurrentTime(0);
        setVideoDuration(0);
        setActiveCameraIndex(0);
        videoRefs.current = [];

        const handleError = (error) => {
            if (error?.name === 'AbortError') return true;
            if (isSessionExpiredError(error)) onSessionExpiredRef.current?.();
            return false;
        };

        // Camera videos come from the Derived bucket; a 404 means the episode is not derived-ready.
        const previewPath = isRejected
            ? `/data/episodes/${episodeId}/rejection-preview${adminMode ? '?admin=true' : ''}`
            : `/data/admin/episodes/${episodeId}/derived-videos`;
        fetch(`${PRISMAX_DATA_PIPELINE_URL}${previewPath}`, {
            headers,
            signal: controller.signal,
        })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (response.status === 404) return [];
                if (!response.ok || !json?.success) {
                    throw new Error(getApiErrorMessage(json, 'Failed to load camera previews.'));
                }
                return (Array.isArray(json?.data?.videos) ? json.data.videos : [])
                    .map((video) => ({...video, url: String(video?.url || video?.signed_url || '').trim()}))
                    .filter((video) => video.url)
                    .sort((left, right) => getCameraOrder(left) - getCameraOrder(right));
            })
            .then((videos) => {
                if (!controller.signal.aborted) setCameraVideos(videos);
            })
            .catch((error) => {
                if (!controller.signal.aborted && !handleError(error)) setPreviewError(error.message || 'Preview unavailable.');
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoadingCameras(false);
            });

        if (!adminMode) {
            setIsLoadingDetail(false);
            return () => controller.abort();
        }
        fetch(`${PRISMAX_DATA_PIPELINE_URL}/data/admin/episodes/${episodeId}/detail`, {
            headers,
            signal: controller.signal,
        })
            .then(async (response) => {
                const json = await response.json().catch(() => ({}));
                if (!response.ok || !json?.success) {
                    throw new Error(getApiErrorMessage(json, 'Failed to load episode details.'));
                }
                if (!controller.signal.aborted) setDetail(json.data || null);
            })
            .catch((error) => {
                if (handleError(error)) return;
                setDetailError(error.message || 'Failed to load episode details.');
            })
            .finally(() => {
                if (!controller.signal.aborted) setIsLoadingDetail(false);
            });

        return () => controller.abort();
    }, [authToken, adminMode, episodeId, isRejected]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopImmediatePropagation();
                onClose?.();
            }
        };
        window.addEventListener('keydown', handleKeyDown, true);
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [onClose]);

    useEffect(() => {
        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, []);

    if (!episode) return null;

    const task = detail?.task || upload?.task || {};
    const title = formatText(task.scenario || upload?.scenario || episode.task_name || `Episode ${episodeId}`);
    const uploadId = Number(upload?.upload_id || episode.upload_id || 0);
    const uploadLabel = upload?.upload_label || (uploadId ? `UPL-${String(uploadId).padStart(4, '0')}` : '-');
    const uploadEpisodeCount = Number(upload?.episode_count || 0);
    const machineName = String(episode.product_name || upload?.product_name || '').trim();
    const isYam = machineName.toLowerCase() === 'yam';
    const episodeSeconds = Number(episode.video_duration_seconds || Number(episode.video_duration_hours || 0) * 3600);
    const durationLabel = episodeSeconds > 0 ? formatClock(Math.max(1, Math.round(episodeSeconds))) : '-';
    const files = Array.isArray(detail?.files) ? detail.files : [];
    const mp4Count = Number.isFinite(Number(episode.video_count)) && episode.video_count !== null
        ? Number(episode.video_count)
        : files.filter((file) => String(file.kind).startsWith('video')).length;
    const playedPercent = videoDuration > 0 ? Math.min(100, (currentTime / videoDuration) * 100) : 0;
    const overviewRows = [
        {label: 'Objectives', value: formatOverviewValue(task.objects)},
        {label: 'Action', value: formatOverviewValue(task.action)},
        {label: 'Variations', value: formatOverviewValue(task.task_variations)},
        {label: 'Notes', value: formatOverviewValue(task.notes)},
    ];
    const description = isLoadingDetail ? 'Loading…' : formatText(task.details_description || task.description);

    const seekAll = (nextTime) => {
        const safeTime = Math.max(0, Math.min(videoDuration || nextTime, nextTime));
        videoRefs.current.filter(Boolean).forEach((video) => {
            if (video.readyState > 0) video.currentTime = safeTime;
        });
        setCurrentTime(safeTime);
    };

    const handleTogglePlayback = () => {
        const videos = videoRefs.current.filter(Boolean);
        if (!videos.length) return;
        if (isPlaying) {
            videos.forEach((video) => video.pause());
            setIsPlaying(false);
            return;
        }
        videos.forEach((video) => {
            void video.play().catch(() => undefined);
        });
        setIsPlaying(true);
    };

    const handlePrimaryTimeUpdate = (event) => {
        const nextTime = event.currentTarget.currentTime;
        setCurrentTime(nextTime);
        videoRefs.current.slice(1).forEach((video) => {
            if (video?.readyState > 0 && Math.abs(video.currentTime - nextTime) > 0.35) {
                video.currentTime = nextTime;
            }
        });
    };

    const handleScrub = (event) => {
        if (!videoDuration) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const ratio = Math.max(0, Math.min(1, (event.clientX - rect.left) / rect.width));
        seekAll(ratio * videoDuration);
    };

    const handleScrubKeyDown = (event) => {
        if (!videoDuration) return;
        let nextTime = currentTime;
        if (event.key === 'ArrowLeft') nextTime -= 5;
        else if (event.key === 'ArrowRight') nextTime += 5;
        else if (event.key === 'Home') nextTime = 0;
        else if (event.key === 'End') nextTime = videoDuration;
        else return;
        event.preventDefault();
        seekAll(nextTime);
    };

    const handleCameraScroll = () => {
        const strip = cameraStripRef.current;
        if (!strip?.children.length) return;
        const panes = Array.from(strip.children);
        const nearestIndex = panes.reduce((nearest, pane, index) => (
            Math.abs(pane.offsetLeft - strip.scrollLeft) < Math.abs(panes[nearest].offsetLeft - strip.scrollLeft)
                ? index
                : nearest
        ), 0);
        setActiveCameraIndex(nearestIndex);
    };

    const handleSelectCamera = (index) => {
        const strip = cameraStripRef.current;
        const pane = strip?.children[index];
        if (!strip || !pane) return;
        strip.scrollTo({left: pane.offsetLeft, behavior: 'smooth'});
        setActiveCameraIndex(index);
    };

    return ReactDOM.createPortal(
        <div
            className={styles.backdrop}
            onClick={(event) => {
                if (event.target === event.currentTarget) onClose?.();
            }}
        >
            <div className={styles.modal} role="dialog" aria-modal="true" aria-labelledby={titleId}>
                <div className={styles.modalHeader}>
                    <div className={styles.modalTitleGroup}>
                        <div id={titleId} className={styles.modalTitle}>{title}</div>
                        <div className={styles.modalSubtitle}>
                            <DatasetIcon/>
                            <span>{uploadLabel}</span>
                            {uploadEpisodeCount > 0 ? <>
                                <span className={styles.subtitleDivider}>·</span>
                                <span>{uploadEpisodeCount.toLocaleString('en-US')} episodes in upload</span>
                            </> : null}
                        </div>
                    </div>
                    <button type="button" className={styles.closeButton} onClick={onClose} aria-label="Close episode details" autoFocus>
                        <svg viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true">
                            <path d="M2 2l10 10M12 2L2 12"/>
                        </svg>
                    </button>
                </div>

                <div className={styles.modalBody}>
                    <div className={styles.cameraWrap}>
                        <div ref={cameraStripRef} className={styles.cameraStrip} onScroll={handleCameraScroll}>
                            {isLoadingCameras ? CAMERA_LOADING_SLOTS.map((slot) => (
                                <div
                                    key={slot}
                                    className={`${styles.cameraPane} ${styles.cameraPaneLoading}`}
                                    role="status"
                                    aria-label={`Loading ${slot} camera preview`}
                                />
                            )) : cameraVideos.length ? cameraVideos.map((video, index) => (
                                <div key={`${video.slot || 'camera'}-${video.object_name || index}`} className={styles.cameraPane}>
                                    <span className={styles.cameraLabel}>{getCameraLabel(video)}</span>
                                    <video
                                        ref={(element) => { videoRefs.current[index] = element; }}
                                        className={`${styles.cameraMedia} ${isYam ? styles.yamPreviewMedia : ''}`}
                                        src={video.url}
                                        poster={index === 0 && previewImageUrl ? previewImageUrl : undefined}
                                        muted
                                        autoPlay
                                        loop
                                        playsInline
                                        preload="metadata"
                                        controlsList="nodownload noplaybackrate noremoteplayback"
                                        onContextMenu={isRejected ? (event) => event.preventDefault() : undefined}
                                        disablePictureInPicture
                                        onPlay={index === 0 ? () => setIsPlaying(true) : undefined}
                                        onPause={index === 0 ? () => setIsPlaying(false) : undefined}
                                        onTimeUpdate={index === 0 ? handlePrimaryTimeUpdate : undefined}
                                        onLoadedMetadata={index === 0
                                            ? (event) => setVideoDuration(Number(event.currentTarget.duration) || 0)
                                            : undefined}
                                    />
                                </div>
                            )) : (
                                <div className={`${styles.cameraPane} ${styles.cameraPaneEmpty}`}>
                                    <span className={styles.cameraEmptyText}>No preview available</span>
                                </div>
                            )}
                        </div>
                        {cameraVideos.length ? (
                            <button
                                type="button"
                                className={`${styles.cameraPlayButton} ${!isPlaying ? styles.cameraPlayButtonVisible : ''}`}
                                onClick={handleTogglePlayback}
                                aria-label={isPlaying ? 'Pause camera videos' : 'Play camera videos'}
                            >
                                {isPlaying ? <span className={styles.pauseIcon}><i/><i/></span> : (
                                    <svg viewBox="0 0 10 10" aria-hidden="true"><polygon points="2,1 9,5 2,9"/></svg>
                                )}
                            </button>
                        ) : null}
                        <span className={styles.cameraDuration}>{durationLabel}</span>
                    </div>

                    {cameraVideos.length ? (
                        <div className={styles.timelineRow}>
                            <span className={styles.timelineTime}>{formatClock(currentTime)}</span>
                            <div
                                className={styles.timeline}
                                role="slider"
                                tabIndex={0}
                                aria-label="Preview playback position"
                                aria-valuemin={0}
                                aria-valuemax={Math.round(videoDuration)}
                                aria-valuenow={Math.round(currentTime)}
                                aria-valuetext={`${formatClock(currentTime)} of ${formatClock(videoDuration)}`}
                                onClick={handleScrub}
                                onKeyDown={handleScrubKeyDown}
                            >
                                <span className={styles.timelinePlayed} style={{width: `${playedPercent}%`}}/>
                                <span className={styles.timelineKnob} style={{left: `${playedPercent}%`}}/>
                            </div>
                            <span className={`${styles.timelineTime} ${styles.timelineTotal}`}>{formatClock(videoDuration)}</span>
                        </div>
                    ) : null}
                    {cameraVideos.length > 1 ? (
                        <div className={styles.cameraDots} aria-label="Camera previews">
                            {cameraVideos.map((video, index) => (
                                <button
                                    type="button"
                                    key={`${video.slot || 'camera'}-dot-${index}`}
                                    className={`${styles.cameraDot} ${index === activeCameraIndex ? styles.cameraDotActive : ''}`}
                                    onClick={() => handleSelectCamera(index)}
                                    aria-label={`Show ${getCameraLabel(video)} camera`}
                                    aria-current={index === activeCameraIndex ? 'true' : undefined}
                                />
                            ))}
                        </div>
                    ) : null}

                    <div className={styles.metaStrip}>
                        <div className={styles.badgeRow}>
                            <span className={`${styles.badge} ${styles.episodeBadge}`}>Episode {formatText(episode.episode_id)}</span>
                            <span className={`${styles.badge} ${styles.qualityBadge}`}>Score {isLoadingDetail ? '…' : formatScore(detail?.qa_score)}</span>
                            <span className={`${styles.badge} ${styles.durationBadge}`}>{durationLabel}</span>
                        </div>
                        {machineName ? (
                            <div className={styles.machineRow}><RobotIcon/><span>{machineName}</span></div>
                        ) : null}
                        <div className={styles.fileRow}>
                            <div className={`${styles.fileChip} ${styles.mp4Chip}`}>
                                <FileIcon/>
                                <span className={styles.fileChipLabel}>MP4</span>
                                <span className={styles.fileChipCount}>×{mp4Count}</span>
                            </div>
                            <div className={`${styles.fileChip} ${styles.mcapChip}`}>
                                <McapIcon/>
                                <span className={styles.fileChipLabel}>MCAP</span>
                                <span className={styles.fileChipCount}>×1</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.infoGrid}>
                        <div className={styles.infoColumn}>
                            <div className={styles.sectionLabel}>Description</div>
                            <p className={styles.description}>{description}</p>
                        </div>
                        <div className={styles.infoColumn}>
                            <div className={styles.sectionLabel}>Overview</div>
                            <div className={styles.overviewGrid}>
                                {overviewRows.map((row) => (
                                    <div key={row.label} className={styles.overviewField}>
                                        <span className={styles.overviewLabel}>{row.label}</span>
                                        <span className={styles.overviewValue}>{isLoadingDetail ? '…' : row.value}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {previewError ? <p role="alert" className={styles.filesError}>{previewError}</p> : null}
                    {isRejected ? (
                        <section className={styles.filesSection} aria-label="Rejection reason">
                            <div className={styles.filesHeader}><span className={styles.filesTitle}>QA rejected</span></div>
                            <p className={styles.description}>{episode.rejection?.reason || 'This episode has been rejected.'}</p>
                            {episode.rejection?.rejected_at ? <p className={styles.footerNote}>{new Date(episode.rejection.rejected_at).toLocaleString()}</p> : null}
                        </section>
                    ) : <section className={styles.filesSection} aria-label="Files">
                        <div className={styles.filesHeader}>
                            <span className={styles.filesTitle}>Files</span>
                            {!isLoadingDetail && !detailError ? (
                                <span className={styles.filesCount}>{files.length} available to download</span>
                            ) : null}
                        </div>
                        <div className={styles.filesScroll}>
                            <div className={`${styles.filesRow} ${styles.filesHead}`}>
                                <span>Kind</span>
                                <span>Name</span>
                                <span className={styles.fileSize}>Size</span>
                                <span>Bucket</span>
                            </div>
                            {isLoadingDetail ? (
                                <div className={styles.filesMessage}>Loading files…</div>
                            ) : detailError ? (
                                <div className={`${styles.filesMessage} ${styles.filesError}`} role="alert">{detailError}</div>
                            ) : !files.length ? (
                                <div className={styles.filesMessage}>No downloadable files found.</div>
                            ) : files.map((file, index) => (
                                <div key={`${file.kind}-${file.name}-${index}`} className={styles.filesRow}>
                                    <span className={styles.fileKind}>{file.kind}</span>
                                    <span className={styles.fileName} title={file.name}>{file.name}</span>
                                    <span className={styles.fileSize}>{formatBytes(file.size_bytes)}</span>
                                    <span className={styles.fileBucket} title={file.bucket}>{file.bucket || '-'}</span>
                                </div>
                            ))}
                        </div>
                    </section>}
                </div>

                <div className={styles.modalFooter}>
                    <span className={styles.footerNote}>
                        {isRejected ? 'This episode was QA rejected and is unavailable for download.' : 'Selected episodes are downloaded from the panel on the right after email verification.'}
                    </span>
                    {!isRejected && <button
                        type="button"
                        className={`${styles.selectButton} ${isSelected ? styles.selectButtonSelected : ''}`}
                        disabled={selectDisabled}
                        onClick={() => onToggleSelect?.()}
                        aria-pressed={isSelected}
                    >
                        <CheckIcon/>
                        {isSelected ? 'Selected for download' : 'Select for download'}
                    </button>}
                </div>
            </div>
        </div>,
        document.body,
    );
}
